import React, { useContext, useState } from 'react';
import ThemeContext from '../contexts/ThemeContext';

import styled from 'styled-components';

import {
  StepsHeader,
  FlexItem,
  StyledErrorMessage,
} from './basicComponents';
import { HeaderFeedbackStep, FooterFeedbackStep } from './HeaderFeedbackStep';
import Spinner from '../components/Spinner';
import { isHeroBrandEnabled } from '../utils/heroBrandUtils';

import i18n from '../i18n';
import Switch from '@material-ui/core/Switch';
import { withStyles } from '@material-ui/core/styles';

const StyledStepsHeader = styled(StepsHeader)`
  color: ${({ isCover, headerColor }) => isCover ? 'white' : (headerColor || '#46446F')};
  background: ${({ isCover, backgroundColor }) => isCover ? (backgroundColor || '#46446F') : 'transparent'};
`;

const ContentContainer = styled.div`
  display: flex;
  flex-flow: column;
  text-align: center;
  height: calc(100vh - 116px);
  background: ${({ gradientColor }) => gradientColor ?
    `linear-gradient(180deg, ${gradientColor} 8%, rgba(246,249,252,1) 8%)` :
    'linear-gradient(180deg, rgba(70,68,111,1) 8%, rgba(246,249,252,1) 8%)'
  };
`;

const TextContainer = styled.div`
  border-radius: 8px;
  padding: 1rem;
  box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 16px;
  width: 80%;
  margin: 0 auto;
  margin-top: 1rem;
  display: flex;
  justify-content: space-around;
  background-color: white;
  flex-flow: column;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledTextarea = styled.textarea`
  padding: 1rem;
  background-color: #f6f9fc;
  border: 1px solid ${({ $hasError, $errorColor }) => $hasError ? $errorColor : 'rgb(216, 216, 220)'};
  resize: none;
  ::placeholder {
    color: rgb(216, 216, 220);
  }
`;

const OPEN_FEEDBACK_ID = 'openFeedback';
const OPEN_FEEDBACK_REQUIRED_MESSAGE_ID = 'openFeedbackRequiredMessage';

const OpenFeedback = ({ contactWanted, handleContactChange, openFeedback, handleOpenFeedbackChange, isOpenFeedbackMandatory }) => {
  const { colors } = useContext(ThemeContext);
  const [isTouched, setIsTouched] = useState(false);

  const showRequiredHint = isOpenFeedbackMandatory && !openFeedback.trim();
  const showRequiredError = showRequiredHint && isTouched;

  const PurpleSwitch = withStyles({
    switchBase: {
      color: '#ccc',
      '&$checked': {
        color: colors.feedbackSwitchColor
      },
      '&$checked + $track': {
        backgroundColor: colors.feedbackSwitchColor
      },
    },
    checked: {},
    track: {
      backgroundColor: '#ccc'
    },
  })(Switch);

  return (
    <TextContainer>
      <StyledTextarea
        id={OPEN_FEEDBACK_ID}
        rows={6}
        cols={50}
        placeholder={i18n('describe_your_experience_here')}
        value={openFeedback}
        onChange={(e) => handleOpenFeedbackChange(e.target.value)}
        onBlur={isOpenFeedbackMandatory ? () => setIsTouched(true) : undefined}
        aria-required={isOpenFeedbackMandatory}
        aria-invalid={showRequiredError}
        aria-describedby={showRequiredHint ? OPEN_FEEDBACK_REQUIRED_MESSAGE_ID : undefined}
        $hasError={showRequiredError}
        $errorColor={colors.errorColor}
      />
      {showRequiredHint && (
        <StyledErrorMessage
          id={OPEN_FEEDBACK_REQUIRED_MESSAGE_ID}
          data-test='openFeedbackRequired'
          $color={showRequiredError ? colors.errorColor : colors.webMediumGray}
        >
          {i18n('invalid_field_value')}
        </StyledErrorMessage>
      )}
      {isHeroBrandEnabled() ? null : (
        <StyledRow>
          <p>{i18n('i_wish_to_be_contacted')}</p>
          <PurpleSwitch
            checked={contactWanted}
            onChange={handleContactChange}
          />
        </StyledRow>
      )}
    </TextContainer>
  );
};

const SecondFeedbackStep = (props) => {
  const { header, nextStep, previousStep, skipStep, contactWanted, handleContactChange, openFeedback, handleOpenFeedbackChange, handleSubmit, indicator, isMobileWidth, isShowThirdStep } = props;
  const { colors } = useContext(ThemeContext);

  const isOpenFeedbackMandatory = isHeroBrandEnabled();
  const isOpenFeedbackRequired = isOpenFeedbackMandatory && !openFeedback.trim();

  const handleNext = () => {
    handleSubmit();
    if (isShowThirdStep) {
      nextStep();
    }
  };

  return (
   <FlexItem>
      <HeaderFeedbackStep
        header={header}
        showPrev={isMobileWidth}
        previousStep={previousStep}
        nextStep={nextStep}
        skipStep={skipStep}
        showContinue={false}
        nextButtonLabel={skipStep ? i18n('skip') : ''}
      />
      <ContentContainer gradientColor={colors.containerColor}>
        {indicator ? (<Spinner overlay />): null}
        <StyledStepsHeader isCover={true} backgroundColor={colors.containerColor} headerColor={colors.containerColor}>
          {i18n('leave_open_feedback')}
          {isOpenFeedbackMandatory && <span aria-label='required'> *</span>}
        </StyledStepsHeader>
        <OpenFeedback
          contactWanted={contactWanted}
          handleContactChange={handleContactChange} 
          openFeedback={openFeedback}
          handleOpenFeedbackChange={handleOpenFeedbackChange}
          isOpenFeedbackMandatory={isOpenFeedbackMandatory}
        />
        <FooterFeedbackStep 
          header={header}
          showPrev={!isMobileWidth}
          showNext={true}
          nextStep={handleNext}
          previousStep={previousStep}
          nextButtonLabel={i18n('proceed')}
          isDisabledNext={isOpenFeedbackRequired}
        />
      </ContentContainer>
   </FlexItem>
  );
};

export default SecondFeedbackStep;
